import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { fetchConfig, onAnimationEnd, mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  /**
   * Handles a swatch click in the hover panel.
   * Color option clicks open the quick-add modal; all other option clicks (e.g. size)
   * resolve the matching variant and add it directly to the cart.
   * Falls back to the modal when a variant is unavailable or cannot be resolved.
   * @param {Event} event - The click event
   */
  handlePanelSwatchClick = async (event) => {
    const btn = /** @type {HTMLElement | null} */ (
      event.target instanceof HTMLElement ? event.target.closest('button[data-option-name]') : null
    );
    if (!btn) return;

    const optionName = btn.dataset.optionName?.toLowerCase() ?? '';
    const colorOptionName = this.dataset.colorOptionName?.toLowerCase() ?? 'color';

    if (optionName === colorOptionName) {
      // Pre-select the matching variant in the card's swatch radios so #syncVariantSelection works
      const variantScriptEl = this.querySelector('.quick-add-panel__variants');
      if (variantScriptEl) {
        /** @type {Array<{id: number, options: string[], available: boolean}>} */
        const variants = JSON.parse(variantScriptEl.textContent ?? '[]');
        const clickedValue = btn.dataset.value ?? '';
        const targetVariant = variants.find((v) => v.options.includes(clickedValue));
        if (targetVariant) {
          const productCard = this.closest('product-card');
          const matchingRadio = /** @type {HTMLInputElement | null} */ (
            productCard?.querySelector(`input[type="radio"][data-variant-id="${targetVariant.id}"]`)
          );
          if (matchingRadio && !matchingRadio.checked) {
            matchingRadio.checked = true;
            matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
      // Color swatch clicked → open the quick-add modal
      await this.handleClick(event);
      return;
    }

    // Size / other option clicked → resolve variant and add to cart directly
    const variantScriptEl = this.querySelector('.quick-add-panel__variants');
    if (!variantScriptEl) {
      await this.handleClick(event);
      return;
    }

    /** @type {Array<{id: number, options: string[], available: boolean}>} */
    const variants = JSON.parse(variantScriptEl.textContent ?? '[]');
    const clickedValue = btn.dataset.value ?? '';

    // Preserve the currently selected color (or other options) from the product card
    const currentVariantId = this.#getSelectedVariantId();
    const currentVariant = currentVariantId ? variants.find((v) => String(v.id) === String(currentVariantId)) : null;

    // Find the variant matching the clicked size while keeping other option values intact
    const targetVariant = variants.find((v) => {
      if (!v.options.includes(clickedValue)) return false;
      if (!v.available) return false;
      if (!currentVariant) return true;
      return v.options.every((optVal, idx) => {
        if (optVal === clickedValue) return true;
        return optVal === currentVariant.options[idx];
      });
    });

    if (!targetVariant) {
      await this.handleClick(event);
      return;
    }

    // POST to cart add endpoint following the same pattern as product-form-component
    const formData = new FormData();
    formData.append('id', String(targetVariant.id));
    formData.append('quantity', '1');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = Array.from(cartItemsComponents)
      .filter((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .join(',');
    if (sectionIds) formData.append('sections', sectionIds);

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        // Server returned an error — fall back to the modal
        await this.handleClick(event);
        return;
      }

      // Dispatch CartAddEvent immediately so the cart count and sections update
      this.dispatchEvent(
        new CartAddEvent({}, String(targetVariant.id), {
          source: 'quick-add-hover-panel',
          itemCount: 1,
          sections: data.sections,
        })
      );

      // Fly-to-cart animation, then open the cart drawer once it lands
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));

      if (cartIcon) {
        const flyToCartElement = /** @type {import('./fly-to-cart.js').default & HTMLElement} */ (
          document.createElement('fly-to-cart')
        );
        const productCard = this.closest('product-card');
        const productImage = productCard?.querySelector('img');

        flyToCartElement.classList.add('fly-to-cart--quick');
        if (productImage?.src) {
          flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
        }
        flyToCartElement.style.setProperty('--start-opacity', '0');
        flyToCartElement.source = btn;
        flyToCartElement.destination = cartIcon;
        document.body.appendChild(flyToCartElement);

        onAnimationEnd(flyToCartElement).then(() => cartDrawer?.showDialog());
      } else {
        cartDrawer?.showDialog();
      }
    } catch {
      await this.handleClick(event);
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
